// src/lib/audit.js

import { supabase } from './supabase'

// ✅ Enhanced audit logging with all critical events
export const AUDIT_EVENTS = {
  // User management
  USER_ROLE_CHANGED: 'user.role_changed',
  USER_CREATED: 'user.created',
  USER_DELETED: 'user.deleted',
  
  // Document permissions
  DOCUMENT_PERMISSION_GRANTED: 'document.permission_granted',
  DOCUMENT_PERMISSION_REVOKED: 'document.permission_revoked',
  
  // Subscription
  SUBSCRIPTION_UPGRADED: 'subscription.upgraded',
  SUBSCRIPTION_DOWNGRADED: 'subscription.downgraded',
  SUBSCRIPTION_CANCELLED: 'subscription.cancelled',
  
  // API Keys
  API_KEY_CREATED: 'api_key.created',
  API_KEY_REVOKED: 'api_key.revoked',
  
  // Security
  LOGIN_FAILED: 'security.login_failed',
  RATE_LIMIT_EXCEEDED: 'security.rate_limit_exceeded',
  SUSPICIOUS_ACTIVITY: 'security.suspicious_activity',
}

// ✅ Get client IP (works in both browser and Edge Functions)
async function getClientIP() {
  try {
    // For browser environments
    if (typeof window !== 'undefined') {
      const res = await fetch('https://api.ipify.org?format=json')
      const data = await res.json()
      return data.ip
    }
    // For Edge Functions - use Deno env
    if (typeof Deno !== 'undefined') {
      // In Edge Functions, IP is in the request headers
      // This will be passed from the caller
      return null
    }
    return 'unknown'
  } catch {
    return 'unknown'
  }
}

// ✅ For Edge Functions - pass IP from request
export async function logSecurityEvent({
  event,
  actor,
  target,
  details,
  company_id,
  severity = 'info',
  ip_address = null, // ✅ Accept IP from caller
  user_agent = null, // ✅ Accept user agent from caller
}) {
  try {
    const ip = ip_address || await getClientIP()
    
    const { error } = await supabase
      .from('audit_logs')
      .insert({
        event,
        actor,
        target,
        details: typeof details === 'string' ? details : JSON.stringify(details),
        company_id,
        severity,
        ip_address: ip,
        user_agent: user_agent,
        created_at: new Date().toISOString(),
      })
    if (error) console.error('Audit log error:', error)
  } catch (err) {
    console.error('Failed to log security event:', err)
  }
}

// ✅ Rest of audit functions remain the same
export async function logAccess({ company_id, investor_id, document_id, action, ip_address, user_agent }) {
  try {
    const { error } = await supabase
      .from('data_access_logs')
      .insert({
        company_id: company_id || null,
        investor_id: investor_id || null,
        document_id: document_id || null,
        action: action || 'unknown',
        ip_address: ip_address || null,
        user_agent: user_agent || null,
      })
    if (error) console.error('Failed to log access:', error.message)
    return !error
  } catch (err) {
    console.error('Audit log error:', err)
    return false
  }
}

export async function logAuditEvent({ company_id, document_id, action, actor, actor_id, details }) {
  try {
    const { error } = await supabase
      .from('audit_logs')
      .insert({
        company_id: company_id || null,
        document_id: document_id || null,
        action: action || 'unknown',
        actor: actor || 'system',
        actor_id: actor_id || null,
        details: details || null,
      })
    if (error) console.error('Failed to log audit event:', error.message)
    return !error
  } catch (err) {
    console.error('Audit log error:', err)
    return false
  }
}

export async function fetchAuditLogs(companyId) {
  const { data, error } = await supabase
    .from('audit_logs')
    .select('*')
    .eq('company_id', companyId)
    .order('created_at', { ascending: false })
    .limit(50)
  if (error) throw error
  return data || []
}